<template>
  <div
    class="play-collapse-item"
    :class="{ 'is-open': isOpen }"
  >
    <button
      class="play-collapse-item__header"
      type="button"
      role="tab"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="toggle"
      @mouseenter="onHoverEnter"
      @mouseleave="onHoverLeave"
    >
      <slot name="header">
        <span class="play-collapse-item__title">{{ title }}</span>
      </slot>

      <span
        class="play-collapse-item__chevron"
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
      </span>
    </button>

    <transition
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div
        v-show="isOpen"
        :id="panelId"
        class="play-collapse-item__panel"
        role="tabpanel"
      >
        <div class="play-collapse-item__body">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * PlayCollapseItem — a single collapsible row for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Used inside
 * <PlayCollapse>; replaces the legacy @core <AppCollapseItem>. The default slot
 * is the body; the `header` slot (or `title` prop) is the always-visible toggle.
 * `open` sets the initial state. Emits `visible` (Boolean) on every change.
 *
 * Auto-height is animated with JS transition hooks (measure scrollHeight, animate
 * height 0 ⇄ content, then release to auto) so content of any size animates
 * smoothly; `prefers-reduced-motion` snaps instead.
 *
 * VUE3-MIGRATION: coordination is via inject('playCollapse') — NOT $parent — so
 * accordion/hover behaviour survives the Vue 3 move untouched. The transition
 * JS hooks and props/slots are already Vue 3-compatible.
 */
let itemUid = 0

export default {
  name: 'PlayCollapseItem',
  inject: {
    playCollapse: { default: null },
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    /** Initial open state */
    open: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    itemUid += 1
    return {
      isOpen: this.open,
      panelId: `play-collapse-panel-${itemUid}`,
    }
  },
  created() {
    if (this.playCollapse) this.playCollapse.register(this)
  },
  beforeDestroy() {
    if (this.playCollapse) this.playCollapse.unregister(this)
  },
  methods: {
    toggle() {
      this.setOpen(!this.isOpen)
    },
    /** Imperative close — called by the group in accordion mode */
    close() {
      this.setOpen(false)
    },
    setOpen(val) {
      if (this.isOpen === val) return
      this.isOpen = val
      if (val && this.playCollapse) this.playCollapse.requestOpen(this)
      this.$emit('visible', val)
    },
    onHoverEnter() {
      if (this.playCollapse && this.playCollapse.hover) this.setOpen(true)
    },
    onHoverLeave() {
      if (this.playCollapse && this.playCollapse.hover) this.setOpen(false)
    },

    // ── Auto-height transition (height 0 ⇄ scrollHeight ⇄ auto) ──
    reducedMotion() {
      return typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    enter(el, done) {
      const node = el
      node.style.height = '0px'
      node.style.overflow = 'hidden'
      if (this.reducedMotion()) {
        done()
        return
      }
      // commit the 0 height, then animate to the measured content height
      requestAnimationFrame(() => {
        node.style.height = `${node.scrollHeight}px`
        node.addEventListener('transitionend', done, { once: true })
      })
    },
    afterEnter(el) {
      const node = el
      node.style.height = ''
      node.style.overflow = ''
    },
    beforeLeave(el) {
      const node = el
      node.style.height = `${node.scrollHeight}px`
      node.style.overflow = 'hidden'
    },
    leave(el, done) {
      const node = el
      if (this.reducedMotion()) {
        node.style.height = '0px'
        done()
        return
      }
      // force a reflow so the explicit start height is committed before collapsing
      // eslint-disable-next-line no-unused-expressions
      node.offsetHeight
      requestAnimationFrame(() => {
        node.style.height = '0px'
        node.addEventListener('transitionend', done, { once: true })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.play-collapse-item {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 18px 20px;
    margin: 0;
    background: transparent;
    border: 0;
    text-align: left;
    cursor: pointer;
    color: var(--text-strong);
    -webkit-tap-highlight-color: transparent;
    transition: background var(--dur-fast) var(--ease-out);

    &:hover { background: var(--c-highlight-soft); }

    &:focus-visible {
      outline: 2px solid var(--c-secondary);
      outline-offset: -3px;
    }
  }

  &__title {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 16px;
    letter-spacing: -.01em;
    line-height: 1.25;
  }

  &__chevron {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-sm);
    background: var(--surface-bg);
    border: var(--border-w) solid var(--border-ink);
    transition: transform var(--dur) var(--ease-bounce), background var(--dur-fast) var(--ease-out);

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: var(--text-strong);
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  &__panel {
    transition: height var(--dur) var(--ease-out);
    will-change: height;
  }

  &__body {
    padding: 0 20px 20px;
    font-family: var(--font-body);
    font-weight: 400;
    color: var(--text-body);
    line-height: 1.55;
  }

  /* ── Open state ── */
  &.is-open .play-collapse-item__chevron {
    transform: rotate(180deg);
    background: var(--c-highlight);
  }

  &.is-open .play-collapse-item__header { background: var(--c-highlight-soft); }
}

@media (prefers-reduced-motion: reduce) {
  .play-collapse-item__chevron,
  .play-collapse-item__panel,
  .play-collapse-item__header { transition: none; }
}
</style>
